import {api} from "../api";
import {FormUtil} from "../forms/formUtil";
import {Combobox} from "../ui/combobox";
import * as BootstrapModal from "@/shared/ui/bootstrapModal";
import * as Button from "@/shared/utils/button";
import * as Input from "@/shared/utils/input";
import {EMPTY_CALLBACK, formatCustomField} from "@/shared/utils/misc";
import {Notify} from "@/shared/ui/notify";
import {Analytics} from "@/shared/analytics";
import {
	calculateBreakTime,
	calculateDurationSince,
	formatDate,
	formatDurationSince,
	formatTime,
	getCurrentISOOffset,
	getISOZonedDateTime,
	isSameDay,
	normalizeTimeInput,
	parseDateInput,
	parseDateTimeZone,
	parseISOZonedDateTime,
	Temporal
} from "@/shared/utils/datefunctions";
import {
	CustomFieldDefinition,
	CustomFieldOption,
	DriveLogStatus,
	ProjectTimeStatus,
	Record as TimrRecord,
	RunningEntry,
	WorkingTimeStatus
} from "@/shared/timr";
import {BreakTimeRecordingMode, CustomFieldsForRecordType, RunningTimeStatus} from "@/shared/timrTypes";

export interface RecordingStatusPanelConfig {
	panel: JQuery;
	fetchStatusURL: string;
	customFieldDefinitions: CustomFieldDefinition[];
}

export type RecordingStatus = WorkingTimeStatus | ProjectTimeStatus | DriveLogStatus;

export abstract class RecordingStatusPanel<
	TStatus extends RecordingStatus,
	TRunningRecording extends RunningEntry,
	TLastRecording extends TimrRecord
> {

	public static EVENT_RECORDING_CHANGED = "recordingChanged";
	public static EVENT_LAST_RECORDING_ID_CHANGED = "lastRecordingIdChanged";
	public static EVENT_STATUS_UPDATED = "statusUpdated";
	public static EVENT_QUICK_SWITCHED = "quickSwitched";
	public static EVENT_QUICK_STARTED = "quickStarted";
	public static EVENT_BUTTON_UPDATED = "buttonUpdated";

	private static BUTTON_CLASS_DEFAULT = "btn-default";
	private static BUTTON_CLASS_PRIMARY = "btn-primary";
	private static BUTTON_CLASS_XLARGE = "btn-xlg";

	protected currentStatus?: TStatus;
	protected changedFields: string[] = [];
	protected currentFocusedField: string;

	private updateTimeout: number = null;
	private waitForUpdateTimeout: number;
	private updateTransaction: JQuery.jqXHR<TStatus> = null;
	private periodicalExecutor: number;
	private doOnEnter: () => void;
	private customFieldComboboxes: Combobox<CustomFieldOption>[] = [];

	constructor(protected config: RecordingStatusPanelConfig) {
		this.doOnEnter = EMPTY_CALLBACK;

		// Firefox restores JS-set hidden inputs on a normal reload (Chrome/Safari don't); a restored
		// recordId/version makes the first status fetch look unchanged, so the running record's fields
		// (note, start/end, ...) are never repopulated and a stale/empty value is submitted on stop. See TIMR-12482.
		this.config.panel.find(".recordId").val(null);
		this.config.panel.find(".version").val(null);

		this.init();
	}

	/**
	 * Fetches the current recording status from the server and updates the panel.
	 * Skipped entirely when either a debounce timeout for a pending user edit is active or an update
	 * request is already in-flight — both guards prevent a stale fetch response from overwriting
	 * unsent or in-transit user changes.
	 *
	 * @param forceUpdate If true, all fields are refreshed even if {@code changedFields} would otherwise
	 *                    protect them. Used on initial page load and after explicit user actions.
	 * @param background  When true, network errors are swallowed silently (no error dialog).
	 *                    Suitable for background polling so that a transient server hiccup does not
	 *                    interrupt the user.
	 */
	public fetchRecordingStatus(forceUpdate = true, background: boolean = false): void {
		this.updateStartTime(this.currentStatus);
		// Do not start a new fetch while a pending edit timeout or an in-flight update transaction
		// is active — the response from our own update will refresh the panel instead.
		if (this.updateTimeout === null && this.updateTransaction === null) {
			api(this.config.fetchStatusURL, background).get({
				localTimeZone: getCurrentISOOffset()
			}).done((data) => {
				this.updateStatus(data as TStatus, forceUpdate);
			});
		}
	}

	public activateUpdater() {
		this.updateEndTime(this.currentStatus);
		this.fetchRecordingStatus(true, true);
		this.startPeriodicalUpdate();
	}

	public deactivateUpdater() {
		this.stopPeriodicalUpdate();
	}

	public getLastRecording(): TLastRecording | null {
		return this.pluckLastRecording(this.currentStatus);
	}

	public getRunningRecording(): TRunningRecording | null {
		return this.pluckRunningRecording(this.currentStatus);
	}

	public getFetchStatusURL(): string {
		return this.config.fetchStatusURL;
	}

	public updateStatus(recordingStatus: TStatus, forceUpdate = true) {
		const runningRecording = this.pluckRunningRecording(recordingStatus);
		this.updateButtons(runningRecording);
		this.updateStatusIndicator(runningRecording);

		// Fire recording changed event if the currently displayed recording in the status panel is a different recording than on the server.
		const currentRunningRecording = this.currentStatus ? this.pluckRunningRecording(this.currentStatus) : null;
		if (currentRunningRecording && (!runningRecording || currentRunningRecording.recordId !== runningRecording.recordId)) {
			this.onRecordingChanged();
		} else if (!currentRunningRecording && runningRecording) {
			// This is also called when the page is loaded. In this case we must not fire the recording changed event because otherwise the table would be loaded twice
			this.clearChanged();
		} else if (currentRunningRecording?.status !== runningRecording?.status) {
			// If pause/resume is clicked, changed flags must also be reset. Otherwise break time isn't increased if it was manually changed before
			this.clearChanged();
		}
		this.onStatusUpdated(recordingStatus);
		this.onTableButtonUpdated(recordingStatus);
		this.currentStatus = recordingStatus;
		this.updateFields(recordingStatus, forceUpdate);
	}

	protected init() {
		this.config.panel.find(".startButton").click(() => {
			this.shouldStartRecording();
		});
		this.config.panel.find(".stopButton").click(() => {
			this.shouldStopRecording();
		});
		this.config.panel.find(".pauseButton").click(() => {
			this.shouldPauseRecording();
		});
		this.config.panel.find(".resumeButton").click(() => {
			this.shouldResumeRecording();
		});
		this.config.panel.find(".deleteButton").click(() => {
			this.shouldDeleteRecording();
		});
		this.config.panel.find(".switchButton").click(() => {
			this.shouldSwitchRecording();
		});

		this.config.panel.find(`input[name^="customField"]`).on("input", () => {
			this.queueUpdate();
		});
		this.config.panel.find(`[id^="customField"]:checkbox`).change(e => {
			const input = $(e.target);
			const hiddenInput = this.config.panel.find(`input[name="${input.val() as string}"]`);
			if (hiddenInput) {
				hiddenInput.val(String(input.is(":checked")));
				this.queueUpdate();
			}
		});
		this.config.panel.find(".description").on("keyup", () => {
			this.queueUpdate();
		});

		const description = this.config.panel.find(".description");
		if (description.length > 0) {
			description.on("input change", e => {
				FormUtil.autoExpand(e.target);
			});
		}

		this.config.panel.keyup(e => {
			const inDescription = this.config.panel.find(".description").is(":focus");
			if (e.which === 13 && (!inDescription || e.ctrlKey)) {
				this.doOnEnter.call(this);
			}
		});

		if (this.isDateTimeChangeAllowed()) {
			this.config.panel.find(".startDate, .endDate, .breakTime, .startTimeZone, .endTimeZone").focus(e => {
				this.currentFocusedField = $(e.target).attr("name");
			}).blur(() => {
				this.currentFocusedField = null;
			});
			this.config.panel.find(".startTime, .endTime").focus(e => {
				this.currentFocusedField = $(e.target).attr("name");
			}).blur(e => {
				this.currentFocusedField = null;
				normalizeTimeInput($(e.target));
			});
			this.config.panel.find(".startDate, .startTime, .startTimeZone, .breakTime").on("input", e => {
				const input = $(e.target);
				if ($.inArray(input.attr("name"), this.changedFields) === -1) {
					this.changedFields[this.changedFields.length] = input.attr("name");
				}
				this.queueUpdate();
			});
			this.config.panel.find(".endDate, .endTime, .endTimeZone").on("input", e => {
				const input = $(e.target);
				if ($.inArray(input.attr("name"), this.changedFields) === -1) {
					this.changedFields[this.changedFields.length] = input.attr("name");
				}
			});

			this.config.panel.find(".focusOnStartDateField").click(() => {
				this.config.panel.find(".startDate").focus();
			});

			this.config.panel.find(".focusOnEndDateField").click(() => {
				this.config.panel.find(".endDate").focus();
			});

			const startTimeDropdownMenu = this.config.panel.find(".startTime").closest(".dropdown").find(".dropdown-menu");
			this.config.panel.find(".startTime")
				.focus(() => {
					const now = Temporal.Now.zonedDateTimeISO();
					const lastRecording = this.pluckLastRecording(this.currentStatus);
					const setNow = startTimeDropdownMenu.find(".setStartToNow");
					const setLastEnd = startTimeDropdownMenu.find(".setStartToLastEnd");

					setNow.find("span").text(`(${formatTime(now)})`);

					// only display last end suggestion if it is the same day of today
					const lastEnd = parseISOZonedDateTime(lastRecording?.end);
					const showLastEnd = isSameDay(lastEnd, now);
					setLastEnd.toggle(showLastEnd);
					if (showLastEnd) {
						setLastEnd.find("span").text(`(${formatTime(lastEnd)})`);
					}
					startTimeDropdownMenu.show();
				})
				.keydown(e => {
					if (e.key === "Tab" || e.key === "Enter") {
						startTimeDropdownMenu.hide();
					}
				})
				.focusout(e => {
					if (!$(e.relatedTarget).is("a.shift-time-entries")) {
						startTimeDropdownMenu.hide();
					}
				});

			const endTimeDropdownMenu = this.config.panel.find(".endTime").closest(".dropdown").find(".dropdown-menu");
			this.config.panel.find(".endTime")
				.focus(() => {
					endTimeDropdownMenu.find(".setEndToNow > a > span").text(`(${formatTime(Temporal.Now.zonedDateTimeISO())})`);
					endTimeDropdownMenu.show();
				}).keydown(e => {
				if (e.keyCode === 9 || e.keyCode === 13) {
					endTimeDropdownMenu.hide();
				}
			}).focusout(e => {
				if (!$(e.relatedTarget).is("a.shift-time-entries")) {
					endTimeDropdownMenu.hide();
				}
			});

			this.config.panel.find(".setStartToLastEnd").prop("disabled", false).click(() => {
				const category = this.pluckRecordingCategory();
				const runningRecording = this.pluckRunningRecording(this.currentStatus) ? "Running" : "Not_Running";
				Analytics.trackEvent(category + " - Set_Start_Time_To_Last_End_Time - " + runningRecording, category);

				const lastRecording = this.pluckLastRecording(this.currentStatus);
				if (lastRecording && lastRecording.end) {
					const startDate = parseISOZonedDateTime(lastRecording.end);
					this.config.panel.find(".startDate").val(formatDate(startDate));
					this.config.panel.find(".startTime").val(formatTime(startDate));
					if ($.inArray("startDate", this.changedFields) === -1) {
						this.changedFields[this.changedFields.length] = "startDate";
					}
					if ($.inArray("startTime", this.changedFields) === -1) {
						this.changedFields[this.changedFields.length] = "startTime";
					}
					this.setRecordingStatusChanged();
					this.showOrHideDateFields();
					this.showOrHideTimeZoneFields();
					this.queueUpdate();
				}
				startTimeDropdownMenu.hide();
			});

			this.config.panel.find(".setStartToNow").prop("disabled", false).click(() => {
				const category = this.pluckRecordingCategory();
				const runningRecording = this.pluckRunningRecording(this.currentStatus) ? "Running" : "Not_Running";
				Analytics.trackEvent(category + " - Set_Start_Time_To_Now - " + runningRecording, category);

				const now = Temporal.Now.zonedDateTimeISO();
				this.config.panel.find(".startDate").val(formatDate(now));
				this.config.panel.find(".startTime").val(formatTime(now));
				this.changedFields = $.grep(this.changedFields, (value) => {
					return value !== "startDate" && value !== "startTime";
				});
				this.showOrHideDateFields();
				this.showOrHideTimeZoneFields();
				this.queueUpdate();
				startTimeDropdownMenu.hide();
			});

			this.config.panel.find(".setEndToNow").prop("disabled", false).click(() => {
				const category = this.pluckRecordingCategory();

				Analytics.trackEvent(category + " - Set_End_Time_To_Now - Running", category);
				const now = Temporal.Now.zonedDateTimeISO();
				this.config.panel.find(".endDate").val(formatDate(now));
				this.config.panel.find(".endTime").val(formatTime(now));
				this.changedFields = $.grep(this.changedFields, (value) => {
					return value !== "endDate" && value !== "endTime";
				});
				this.queueUpdate();
				endTimeDropdownMenu.hide();
			});

			this.config.panel.find(".startDate, .startTime, .startTimeZone, .endDate, .endTime, .endTimeZone").on("input", () => {
				this.setRecordingStatusChanged();
			});

			this.config.panel.find(".startDate").on("apply.daterangepicker", () => {
				this.setRecordingStatusChanged();
				if ($.inArray("startDate", this.changedFields) === -1) {
					this.changedFields[this.changedFields.length] = "startDate";
					this.queueUpdate();
				}
			});
			this.config.panel.find(".endDate").on("apply.daterangepicker", () => {
				this.setRecordingStatusChanged();
				if ($.inArray("endDate", this.changedFields) === -1) {
					this.changedFields[this.changedFields.length] = "endDate";
					this.queueUpdate();
				}
			});
		}
		// https://developer.mozilla.org/en-US/docs/Web/API/WindowEventHandlers/onbeforeunload
		window.addEventListener("beforeunload", e => {
			if (this.updateTimeout != null || this.updateTransaction != null) {
				e.preventDefault();
				// Only ie shows a custom message
				e.returnValue = messages["recording.unsavedChanges.alert"];
				return messages["recording.unsavedChanges.alert"];
			}
		});

		this.config.panel.find("hidden").prop("tabindex", "-1");

		this.initCustomFieldComboboxes();
		this.startPeriodicalUpdate();
		this.startTimeUpdater();
	}

	protected initCustomFieldComboboxes() {
		if (this.config.panel.find(".form-group-custom-field").length !== 0) {
			for (const definition of this.config.customFieldDefinitions) {
				if (definition.fieldType === CustomFieldsForRecordType.Options && definition.fieldName) {
					const customFieldComboboxItems: CustomFieldOption[] = [];
					for (const item of definition.optionsList ?? []) {
						customFieldComboboxItems.push({id: item});
					}
					const customFieldCombobox = new Combobox({
						items: customFieldComboboxItems,
						formatter: (item) => {
							return item.id;
						},
						idElement: this.config.panel.find(`[name="customField${definition.fieldNumber}"]`),
						filterElement: this.config.panel.find(`[id^=custom][id$=Field${definition.fieldNumber}FilterStatusPanel]`),
						caretElement: this.config.panel.find(`[id="customField${definition.fieldNumber}Caret"]`),
						clearable: !definition.mandatory
					});
					this.customFieldComboboxes.push(customFieldCombobox);
					$(customFieldCombobox).on(Combobox.EVENT_CHANGED, () => {
						this.queueUpdate();
					});
				}
			}
		}
	}

	protected abstract pluckRunningRecording(recordingStatus?: TStatus): TRunningRecording | null;

	protected abstract pluckLastRecording(recordingStatus?: TStatus): TLastRecording | null;

	protected abstract pluckRecordingCategory(): string;

	protected abstract isDateTimeChangeAllowed(): boolean;

	protected queueUpdate() {
		if (this.pluckRunningRecording(this.currentStatus)) {
			if (this.updateTimeout != null && this.updateTransaction == null) {
				clearTimeout(this.updateTimeout);
				this.updateTimeout = null;
			}
			this.waitForUpdateToFinish(() => {
				this.updateTimeout = setTimeout(() => {
					this.doSendUpdate();
				}, 1500);
			});
		}
	}

	protected waitForUpdateToFinish(callback: () => void) {
		if (this.updateTimeout == null && this.updateTransaction == null) {
			// TODO: TIMR-9682 Fix ESLint Error: Review typing of callback
			callback.call(this);
		} else if (!this.waitForUpdateTimeout) {
			this.waitForUpdateTimeout = setTimeout(() => {
				this.waitForUpdateTimeout = null;
				this.waitForUpdateToFinish(callback);
			}, 100);
		}
	}

	protected immediatelyUpdateIfNecessary(completed: () => void) {
		if (this.updateTimeout == null && this.updateTransaction == null) {
			completed.call(this);
		} else {
			this.doSendUpdate(completed);
		}
	}

	protected cancelUpdateIfNecessary() {
		clearTimeout(this.updateTimeout);
		this.updateTimeout = null;
	}

	private doSendUpdate(completed: () => void = null) {
		if (this.updateTimeout) {
			clearTimeout(this.updateTimeout);
			this.updateTimeout = null;
		}
		if (this.pluckRunningRecording(this.currentStatus)) {
			this.updateTransaction = this.sendUpdate();
			this.updateTransaction.always(() => {
				this.updateTransaction = null;
				if (completed) {
					completed.call(this);
				}
			});
		} else if (completed) {
			completed.call(this);
		}
	}

	protected abstract sendUpdate(): JQuery.jqXHR<TStatus>;

	protected showStatusUpdateMessage(message: string) {
		Notify.success(message);
	}

	protected clearChanged() {
		this.changedFields = [];
	}

	protected notChanged(fieldId: string): boolean {
		return $.inArray(fieldId, this.changedFields) === -1;
	}

	protected notFocused(fieldId: string): boolean {
		return this.currentFocusedField !== fieldId;
	}

	protected shouldUpdateEndDateAndTime(): boolean {
		return true;
	}

	protected abstract validateSecurityStatus(): boolean;

	protected validateStart(): boolean {
		if (!this.validateSecurityStatus()) {
			return false;
		}
		normalizeTimeInput(this.config.panel.find(".startTime"));
		const startDateTime = this.parseStartDateTime();
		if (startDateTime == null) {
			BootstrapModal.error({
				message: "invalid.start.datetime"
			});
			return false;
		}
		const startTimeZone = this.config.panel.find(".startTimeZone").val() as string;
		if (!globals.timeZonePattern.test(startTimeZone)) {
			BootstrapModal.error({
				message: "invalid.start.timezone"
			});
			return false;
		}
		this.config.panel.find(".startDateTime").val(getISOZonedDateTime(startDateTime));
		return true;
	}

	protected validateStop(): boolean {
		if (!this.validateStart()) {
			return false;
		}
		normalizeTimeInput(this.config.panel.find(".endTime"));
		const endDateTime = this.parseEndDateTime();
		if (endDateTime === null) {
			BootstrapModal.error({
				message: "invalid.end.datetime"
			});
			return false;
		}
		const endTimeZone = this.config.panel.find(".endTimeZone").val() as string;
		if (!globals.timeZonePattern.test(endTimeZone)) {
			BootstrapModal.error({
				message: "invalid.end.timezone"
			});
			return false;
		}
		this.config.panel.find(".endDateTime").val(getISOZonedDateTime(endDateTime));
		return true;
	}

	protected autoCorrectEndDateTime() {
		this.config.panel.find(".endDate").val(this.config.panel.find(".startDate").val());
		const startDateTime = this.parseStartDateTime();
		let endDateTime = this.parseEndDateTime();
		if (endDateTime !== null && startDateTime !== null && Temporal.ZonedDateTime.compare(endDateTime, startDateTime) < 0) {
			endDateTime = endDateTime.add({days: 1});
			this.config.panel.find(".endDate").val(formatDate(endDateTime));
		}
		this.config.panel.find(".endDateTime").val(getISOZonedDateTime(endDateTime));
		// Consider autocorrected times as changed
		this.setRecordingStatusChanged();
	}

	protected prepareStopRecording(success: () => void) {
		if (!this.validateStop()) {
			return;
		}
		const _endDt = this.parseEndDateTime();
		const _startDt = this.parseStartDateTime();
		if (_endDt !== null && _startDt !== null && _startDt.until(_endDt).total({unit: 'hours'}) > 24
			&& this.isDateTimeChangeAllowed()) {
			BootstrapModal.confirm({
				title: "recording.warning.header",
				message: "recording.length.warning",
				buttons: [{
					text: "dialog.save",
					action: (modal: BootstrapModal.Modal, button?: JQuery) => {
						success();
						modal.close();
					},
					css: "btn-primary"
				}, {
					text: "dialog.autocorrect",
					action: (modal: BootstrapModal.Modal, button?: JQuery) => {
						this.autoCorrectEndDateTime();
						success();
						modal.close();
					},
				}, {
					text: "dialog.cancel"
				}]
			});
		} else {
			success();
		}
	}

	protected shouldStartRecording() {
		if (this.validateStart()) {
			const startButton = this.config.panel.find(".startButton");
			this.setLoading(startButton);
			this.startRecording().always(() => {
				this.resetLoading(startButton);
			});
		}
	}

	protected shouldPauseRecording() {
		this.prepareStopRecording(() => {
			const pauseButton = this.config.panel.find(".pauseButton");
			this.setLoading(pauseButton);
			this.immediatelyUpdateIfNecessary(() => {
				this.startPauseRecording();
			});
		});
	}

	protected shouldResumeRecording() {
		this.prepareStopRecording(() => {
			const resumeButton = this.config.panel.find(".resumeButton");
			this.setLoading(resumeButton);
			this.immediatelyUpdateIfNecessary(() => {
				this.startResumeRecording();
			});
		});
	}

	protected shouldStopRecording() {
		this.prepareStopRecording(() => {
			this.cancelUpdateIfNecessary();
			const stopButton = this.config.panel.find(".stopButton");
			this.setLoading(stopButton);
			this.stopRecording().always(() => {
				this.resetLoading(stopButton);
			});
		});
	}

	protected shouldDeleteRecording() {
		const runningRecording = this.pluckRunningRecording(this.currentStatus);
		const duration = runningRecording ? calculateDurationSince(runningRecording.start, runningRecording.breakTime) : null;

		const performDeleteRecording = () => {
			this.cancelUpdateIfNecessary();
			const deleteButton = this.config.panel.find(".deleteButton");
			this.setLoading(deleteButton);
			this.deleteRecording().always(() => {
				this.resetLoading(deleteButton);
			});
		};

		// Only show the confirmation dialog if the recording was started at least 1 minute ago
		if (duration !== null && duration.total('minutes') >= 1) {
			BootstrapModal.confirm({
				title: "recording.delete.title",
				message: "recording.delete.message",
				yesLabel: "dialog.delete",
				yesAction: (modal) => {
					performDeleteRecording();
					modal.close();
				},
				noLabel: "dialog.cancel"
			});
		} else {
			performDeleteRecording();
		}
	}

	protected shouldSwitchRecording() {
		this.prepareStopRecording(() => {
			this.setLoading(this.config.panel.find(".switchButton"));
			this.immediatelyUpdateIfNecessary(() => {
				this.startSwitchRecording();
			});
		});
	}


	public shouldQuickSwitchRecording(recordingId: number, onStart: () => void, onEnd: () => void) {
		this.prepareStopRecording(() => {
			onStart();

			if (this.pluckRunningRecording(this.currentStatus)) {
				this.setLoading(this.config.panel.find(".switchButton"));
			} else {
				this.setLoading(this.config.panel.find(".startButton"));
			}
			this.immediatelyUpdateIfNecessary(() => {
				this.startQuickSwitchRecording(recordingId).always(() => {
					onEnd();
				});
			});
		});
	}

	protected abstract startRecording(): JQuery.jqXHR<TStatus>;

	protected abstract startPauseRecording(): JQuery.jqXHR<TStatus>;

	protected abstract startResumeRecording(): JQuery.jqXHR<TStatus>;

	protected abstract stopRecording(): JQuery.jqXHR<TStatus>;

	protected abstract deleteRecording(): JQuery.jqXHR<TStatus>;

	// currently only used with type Task
	protected abstract startSwitchRecording(recordingType?: unknown): JQuery.jqXHR<TStatus>;

	protected abstract startQuickSwitchRecording(recordingId: number): JQuery.jqXHR<TStatus>;

	protected endSwitchRecording() {
		this.resetLoading(this.config.panel.find(".switchButton"));
	}

	protected endQuickStartRecording() {
		this.resetLoading(this.config.panel.find(".startButton"));
	}

	protected endPauseRecording() {
		this.resetLoading(this.config.panel.find(".pauseButton"));
	}

	protected endResumeRecording() {
		this.resetLoading(this.config.panel.find(".resumeButton"));
	}

	protected updateRecordingInfoPanel(recording: TRunningRecording | null, running: boolean, forceUpdate = true) {
		// maybe overridden in derived classes
	}

	protected parseStartDateTime(): Temporal.ZonedDateTime | null {
		return parseDateTimeZone(
			this.config.panel.find(".startDate").val() as string,
			this.config.panel.find(".startTime").val() as string,
			this.config.panel.find(".startTimeZone").val() as string
		);
	}

	protected parseEndDateTime(): Temporal.ZonedDateTime | null {
		return parseDateTimeZone(
			this.config.panel.find(".endDate").val() as string,
			this.config.panel.find(".endTime").val() as string,
			this.config.panel.find(".endTimeZone").val() as string
		);
	}

	protected clearFields() {
		this.config.panel.find(".recordId").val("");
		this.config.panel.find(".version").val("-1");
		this.config.panel.find(".description").val("");
		this.config.panel.find(".breakTime").val("");
		this.config.panel.find(".changed").val(String(false));
		this.clearCustomFields();
	}

	protected clearCustomFields() {
		// clear inputs for text and number (CustomFieldsForRecordType.String and Number)
		this.config.panel.find(`
				input[data-custom-field-type="${CustomFieldsForRecordType.String}"], 
				input[data-custom-field-type="${CustomFieldsForRecordType.Number}"]`
		).val("");

		// clear inputs for checkboxes (CustomFieldsForRecordType.Checkbox)
		this.config.panel.find(`input[data-custom-field-type="${CustomFieldsForRecordType.Checkbox}"]`).val("false");
		this.config.panel.find(`[id^="customField"]:checkbox`).prop("checked", false);

		// clear inputs for comboboxes (CustomFieldsForRecordType.Options)
		this.customFieldComboboxes.forEach((combobox) => {
			combobox.setEmptyMessage("");
			combobox.clearSelection(true);
		});
	}

	protected updateFieldsWithRunningRecording(runningRecording: TRunningRecording, updateCustomFields = true) {
		this.config.panel.find(".recordId").val(runningRecording.recordId);
		this.config.panel.find(".version").val(runningRecording.version);
		this.config.panel.find(".description").val(runningRecording.description);
		this.config.panel.find(".breakTime").val(runningRecording.breakTime);
		this.config.panel.find(".changed").val(String(runningRecording.changed));

		if (updateCustomFields && this.config.panel.find(".form-group-custom-field").length !== 0) {
			let i = 0;
			for (const definition of this.config.customFieldDefinitions) {
				const value = runningRecording[`customField${definition.fieldNumber}`] ?? "";
				if (definition.fieldType === CustomFieldsForRecordType.Checkbox) {
					const checked = "true" === value;
					this.config.panel.find(`#customField${definition.fieldNumber}CheckboxStatusPanel`).prop("checked", checked);
					this.config.panel.find(`[name="customField${definition.fieldNumber}"]`).val(checked.toString());
				} else if (definition.fieldType === CustomFieldsForRecordType.Number) {
					this.config.panel.find(`[name="customField${definition.fieldNumber}Number"]`).val(formatCustomField(value, definition.fieldType));
				} else if (definition.fieldType === CustomFieldsForRecordType.Options && definition.fieldName && this.customFieldComboboxes[i]) {
					const items = definition.optionsList.map(item => {
						if (item !== "") {
							return {id: item};
						}
					});
					this.customFieldComboboxes[i].updateItems(items);
					if (value === "") {
						if (definition.mandatory) {
							this.customFieldComboboxes[i].setEmptyMessage(messages["recording.customFieldCombobox.empty"]);
							this.customFieldComboboxes[i].clearSelection(true);
						} else {
							this.customFieldComboboxes[i].setEmptyMessage("");
							this.customFieldComboboxes[i].clearSelection();
						}
					} else {
						this.customFieldComboboxes[i].setSelectedItem({id: value}, false, true, false);
					}
					i++;
				} else {
					this.config.panel.find(`[name="customField${definition.fieldNumber}"]`).val(formatCustomField(value, definition.fieldType));
				}
			}
		}
	}

	protected abstract updatePanelBorderColor(): void;

	protected updateFields(recordingStatus: TStatus, forceUpdate = true) {
		this.config.panel.find(".breakTime").prop("readonly", false);
		const runningRecording = this.pluckRunningRecording(recordingStatus);
		if (runningRecording) {
			this.config.panel.find(".runningRecordingField").show();

			// record is considered as changed if either the record ID or the version has changed (background)
			const isRecordingChanged = this.config.panel.find(".recordId").val() !== runningRecording.recordId ||
				parseInt(this.config.panel.find(".version").val() as string, 10) !== runningRecording.version;

			if (isRecordingChanged) {
				this.updateFieldsWithRunningRecording(runningRecording);
			}

			const startDate = parseISOZonedDateTime(runningRecording.start);
			if (isRecordingChanged || (this.notChanged("startDate") && this.notFocused("startDate") &&
				this.notChanged("startTime") && this.notFocused("startTime") &&
				this.notChanged("startTimeZone") && this.notFocused("startTimeZone"))) {
				this.config.panel.find(".startDate").val(formatDate(startDate));
				this.config.panel.find(".startTime").val(formatTime(startDate));
				this.config.panel.find(".startTimeZone").val(startDate?.offset ?? "");
			}
			this.updateEndTime(recordingStatus);
			this.updateRecordingInfoPanel(runningRecording, true, forceUpdate);
		} else {
			this.config.panel.find(".runningRecordingField").hide();
			this.config.panel.find(".breakTimeField").hide();
			Input.enable(this.config.panel.find(".breakTime"));
			this.config.panel.find(".duration").text("").hide();
			if (parseInt(this.config.panel.find(".version").val() as string, 10) !== -1) {
				this.clearFields();
			}
			this.updateStartTime(recordingStatus);
			this.updateEndTime(recordingStatus);
		}
		this.showOrHideDateFields();
		this.showOrHideTimeZoneFields();
		const lastRecording = this.pluckLastRecording(recordingStatus);
		if (lastRecording && lastRecording.id) {
			this.onLastRecordingIdChanged(lastRecording.id);
		}
		// timeout before trigger autoExpand, to get the correct height value
		this.config.panel.find(".description").change();
	}

	protected updateTime() {
		this.updateStartTime(this.currentStatus);
		this.updateEndTime(this.currentStatus);
		this.showOrHideDateFields();
		this.showOrHideTimeZoneFields();
	}

	protected updateStartTime(recordingStatus?: TStatus) {
		if (!this.pluckRunningRecording(recordingStatus) &&
			this.notChanged("startDate") && this.notFocused("startDate") &&
			this.notChanged("startTime") && this.notFocused("startTime") &&
			this.notChanged("startTimeZone") && this.notFocused("startTimeZone")) {
			const now = Temporal.Now.zonedDateTimeISO();
			this.config.panel.find(".startDate").val(formatDate(now));
			this.config.panel.find(".startTime").val(formatTime(now));
			this.config.panel.find(".startTimeZone").val(now.offset);
		}
	}

	protected updateEndTime(recordingStatus?: TStatus) {
		if (this.notChanged("endDate") && this.notFocused("endDate") &&
			this.notChanged("endTime") && this.notFocused("endTime") &&
			this.notChanged("endTimeZone") && this.notFocused("endTimeZone")) {
			const now = Temporal.Now.zonedDateTimeISO();
			this.config.panel.find(".endDate").val(formatDate(now));
			this.config.panel.find(".endTime").val(formatTime(now));
			this.config.panel.find(".endTimeZone").val(now.offset);
		}
		const runningRecording = this.pluckRunningRecording(recordingStatus);
		if (runningRecording) {
			let breakTime = runningRecording.breakTime;
			if (runningRecording.status === RunningTimeStatus.PAUSED) {
				this.config.panel.find(".breakTime").attr("readonly", "readonly");
			}
			if (runningRecording.status === RunningTimeStatus.PAUSED &&
				this.notChanged("breakTime") && this.notFocused("breakTime")) {
				breakTime = calculateBreakTime(runningRecording);
				this.config.panel.find(".breakTime").val(breakTime);
			}
			if (globals.breakTimeRecordingMode === BreakTimeRecordingMode.RECORD_NO_POSITION || breakTime !== 0) {
				this.config.panel.find(".breakTimeField").show();
			} else {
				this.config.panel.find(".breakTimeField").hide();
			}
			if (globals.breakTimeRecordingMode !== BreakTimeRecordingMode.RECORD_NO_POSITION) {
				Input.disable(this.config.panel.find(".breakTime"));
			}
			this.config.panel.find(".duration").text(formatDurationSince(runningRecording.start, breakTime, false)).show();
			this.onStatusUpdated(recordingStatus);
		}
	}

	protected updateButtons(runningRecording: TRunningRecording | null) {
		if (runningRecording) {
			// isBreakTime is only true if the recording is running, and the recording is of type "workingTime"
			// and the working time type is a break time
			// this is in here to prevent flickering
			if (!this.isPaused(runningRecording)) {
				if (globals.manualBreaksAllowed) {
					this.config.panel.find(".startButton, .resumeButton").hide();
					this.config.panel.find(".stopButton, .pauseButton, .deleteButton, .switchButton, div[data-duration-pulse]").show();
				} else {
					this.config.panel.find(".startButton, .pauseButton, .resumeButton").hide();
					this.config.panel.find(".stopButton, .deleteButton, .switchButton, div[data-duration-pulse]").show();
				}
				this.doOnEnter = () => {
					this.waitForUpdateToFinish(() => this.shouldSwitchRecording());
				};
				const switchButton = this.config.panel.find(".switchButton");
				if (switchButton.length > 0) {
					this.setButtonAsPrimary(switchButton, true);
				} else {
					this.setButtonAsPrimary(this.config.panel.find(".stopButton"), true);
				}
				this.setButtonAsPrimary(this.config.panel.find(".startButton, .resumeButton"), false);
			} else if (this.isPaused(runningRecording)) {
				this.config.panel.find(".startButton, .pauseButton").hide();
				this.config.panel.find(".stopButton, .resumeButton, .deleteButton, .switchButton").show();
				if (this.isBreakTime(runningRecording)) {
					this.config.panel.find(".stopButton").hide();
				}
				this.doOnEnter = () => {
					this.waitForUpdateToFinish(() => this.shouldResumeRecording());
				};
				this.setButtonAsPrimary(this.config.panel.find(".resumeButton"), true);
				this.setButtonAsPrimary(this.config.panel.find(".startButton, .stopButton, .switchButton"), false);
			}
		} else {
			this.config.panel.find(".stopButton, .pauseButton, .resumeButton, .deleteButton, .switchButton, div[data-duration-pulse]").hide();
			this.config.panel.find(".startButton").show();
			this.doOnEnter = () => {
				this.waitForUpdateToFinish(() => this.shouldStartRecording());
			};
			this.setButtonAsPrimary(this.config.panel.find(".startButton"), true);
			this.setButtonAsPrimary(this.config.panel.find("stopButton, .resumeButton, .switchButton"), false);
		}
	}

	protected onStatusUpdated(recordingStatus: TStatus) {
		$(this).triggerHandler(RecordingStatusPanel.EVENT_STATUS_UPDATED, [recordingStatus]);
	}

	private onLastRecordingIdChanged(lastRecordingId: number) {
		$(this).triggerHandler(RecordingStatusPanel.EVENT_LAST_RECORDING_ID_CHANGED, [lastRecordingId]);
	}

	private startTimeUpdater() {
		setTimeout(() => {
			this.updateTime();
			this.continueTimeUpdater();
		}, 60000 - (Date.now() % 60000));
	}

	private continueTimeUpdater() {
		setInterval(() => {
			this.updateTime();
		}, 60 * 1000);
	}

	private startPeriodicalUpdate() {
		this.stopPeriodicalUpdate();
		this.periodicalExecutor = setInterval(() => {
			this.fetchRecordingStatus(false, true);
		}, 20000);
	}

	private stopPeriodicalUpdate() {
		if (this.periodicalExecutor) {
			clearInterval(this.periodicalExecutor);
			this.periodicalExecutor = null;
		}
	}

	private showOrHideDateFields() {
		const showDateFields = this.config.panel.find(".startDate, .endDate").filter(function (this: HTMLElement) {
			const value = $(this).val() as string;
			return !isSameDay(parseDateInput(value), Temporal.Now.plainDateISO());
		}).length > 0;
		this.config.panel.find(".status-panel-columns").toggleClass("dateFieldShown", showDateFields);
		this.config.panel.find(".dateField").toggle(showDateFields);
		this.config.panel.find(".dateFieldNot").toggle(!showDateFields);
	}

	private showOrHideTimeZoneFields() {
		const showTimeZones = globals.showTimeZone ||
			(this.config.panel.find(".startTimeZone").val() as string) !== (this.config.panel.find(".endTimeZone").val() as string);
		this.config.panel.find(".status-panel-columns").toggleClass("timeZoneFieldShown", showTimeZones);
		this.config.panel.find(".timeZoneField").toggle(showTimeZones);
		if (!showTimeZones) {
			this.config.panel.find(".startTime, .endTime").addClass("border-radius-right");
		}
	}

	private updateStatusIndicator(runningRecording: TRunningRecording | null) {
		const pulsingStatusIndicator = this.config.panel.find("div[data-duration-pulse]");
		pulsingStatusIndicator.find("div[data-pulse]").toggle(!this.isPaused(runningRecording));
		pulsingStatusIndicator.find(".icon-paused").toggle(this.isPaused(runningRecording));
	}

	protected isBreakTime(recording: TRunningRecording | null) {
		return false;
	}

	protected isPaused(recording: TRunningRecording | null) {
		return this.isBreakTime(recording) ||
			recording?.status === RunningTimeStatus.PAUSED;
	}

	private onRecordingChanged() {
		this.clearChanged();
		$(this).triggerHandler(RecordingStatusPanel.EVENT_RECORDING_CHANGED);
	}

	private onTableButtonUpdated(recordingStatus: TStatus) {
		$(this).triggerHandler(RecordingStatusPanel.EVENT_BUTTON_UPDATED, [!!this.pluckRunningRecording(recordingStatus), recordingStatus]);
	}

	private setRecordingStatusChanged() {
		this.config.panel.find(".changed").val(String(true));
	}

	private setButtonAsPrimary(button: JQuery, primary: boolean) {
		button.addClass(primary ? RecordingStatusPanel.BUTTON_CLASS_PRIMARY : RecordingStatusPanel.BUTTON_CLASS_DEFAULT);
		button.removeClass(primary ? RecordingStatusPanel.BUTTON_CLASS_DEFAULT : RecordingStatusPanel.BUTTON_CLASS_PRIMARY);
		button.toggleClass(RecordingStatusPanel.BUTTON_CLASS_XLARGE, primary);
	}

	protected setLoading(button: JQuery) {
		this.config.panel.find("form > fieldset").prop("disabled", true);
		Button.setLoading(button);
	}

	protected resetLoading(button: JQuery) {
		Button.resetLoading(button);
		this.config.panel.find("form > fieldset").prop("disabled", false);
	}

}
